<script>
import Form from '@classes/Form'
import { medicalDateFormatter, isBeforeDate } from '@utils/medical-date'
import { authComputed, authMethods, optionsComputed } from '@state/helpers'
import { medicationService } from '@/src/services/MedicationService'
import { supplementService } from '@/src/services/SupplementService'
import { healthRecordService } from '@services/HealthRecordService'
import store from '@state/store'
import ComboBox from '@components/combo-box.vue'
import MedicalDatePickerDialog from '@components/dialogs/medical-date-picker-dialog.vue'
import hrMixin from '@src/mixins/hr-mixin.vue'
export default {
  page: {
    title: 'Medication Editor',
    meta: [{ name: 'description', content: 'The Medication Editor page.' }],
  },
  components: {
    ComboBox,
    MedicalDatePickerDialog,
  },
  mixins: [hrMixin],
  props: {
    formDialog: {
      type: Boolean,
      default: false,
    },
    formChanged: {
      type: Boolean,
      default: false,
    },
    formSaved: {
      type: Boolean,
      default: false,
    },
    formSavedAnother: {
      type: Boolean,
      default: false,
    },
    attachmentDeleted: {
      type: Boolean,
      default: false,
    },
    create: {
      type: Boolean,
      default: false,
    },
    edit: {
      type: Boolean,
      default: false,
    },
    item: {
      type: Object,
      default: null,
    },
    id: {
      type: [Number, String],
      default: 0,
    },
    resource: {
      type: String,
      default: 'medication',
    },
    show: {
      type: Boolean,
      default: false,
    },
    unauth: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      row: 'medication',
      medicationForm: new Form({
        medication: '',
        start: null,
        start_date_part: '',
        end: null,
        end_date_part: '',
        medication_stop_date: null,
        medication_stop_date_part: '',
        dosage: '',
        dosage_form: '',
        medication_stop: '',
        medication_stop_reason: '',
        frequency: '',
        interval: '',
        concentration: '',
        continuous: null,
        duration_frequency: '',
        duration_interval: '',
        unit: '',
        note: '',
        private: false,
      }),
      supplementForm: new Form({
        supplement: '',
        start: null,
        start_date_part: '',
        end: null,
        end_date_part: '',
        supplement_stop_date: null,
        supplement_stop_date_part: '',
        dosage: '',
        dosage_form: '',
        supplement_stop: '',
        supplement_stop_reason: '',
        frequency: '',
        interval: '',
        concentration: '',
        continuous: null,
        duration_frequency: '',
        duration_interval: '',
        unit: '',
        note: '',
        private: false,
      }),
      medicationEnd: null,
      supplementEnd: null,
      haveStoppedMedication: '',
      haveStoppedSupplement: '',
      medicationError: null,
      medicalDateStartPickerDialog: false,
      medicalDateEndPickerDialog: false,
      medicationAttachments: [],
      originalMedicationAttachments: [],
      supplementAttachments: [],
      originalSupplementAttachments: [],
      dosageText: {
        1: 'one',
        2: 'two',
        3: 'three',
        4: 'four',
        5: 'five',
        6: 'six',
        7: 'seven',
        8: 'eight',
        9: 'nine',
        10: 'ten',
      },
      showHRActions: true,
    }
  },
  computed: {
    ...authComputed,
    ...optionsComputed,
    medicationIntervals() {
      return this.getMedicationOptions?.interval
        ? this.getMedicationOptions.interval
        : []
    },
    medicationStopReasons() {
      return this.getMedicationOptions?.medication_stop_reason
        ? this.getMedicationOptions.medication_stop_reason
        : []
    },
    medicationUnits() {
      return this.getMedicationOptions?.unit
        ? this.getMedicationOptions.unit
        : []
    },
    medicationDosageForms() {
      return this.getMedicationOptions?.dosage_form
        ? this.getMedicationOptions.dosage_form
        : []
    },
    medicationDurationFrequencies() {
      return this.getMedicationOptions?.duration_frequency
        ? this.getMedicationOptions.duration_frequency
        : []
    },
    medicationDurationIntervals() {
      return this.getMedicationOptions?.duration_interval
        ? this.getMedicationOptions.duration_interval
        : []
    },
    supplementIntervals() {
      return this.getSupplementOptions?.interval
        ? this.getSupplementOptions.interval
        : []
    },
    supplementStopReasons() {
      return this.getSupplementOptions?.supplement_stop_reason
        ? this.getSupplementOptions.supplement_stop_reason
        : []
    },
    supplementDosageForms() {
      return this.getSupplementOptions?.dosage_form
        ? this.getSupplementOptions.dosage_form
        : []
    },
    supplementUnits() {
      return this.getSupplementOptions?.unit
        ? this.getSupplementOptions.unit
        : []
    },
    supplementDurationFrequencies() {
      return this.getSupplementOptions?.duration_frequency
        ? this.getSupplementOptions.duration_frequency
        : []
    },
    supplementDurationIntervals() {
      return this.getSupplementOptions?.duration_interval
        ? this.getSupplementOptions.duration_interval
        : []
    },
    canSaveMedication() {
      if (
        (this.medicationForm.start ||
          this.medicationForm.start_date_part === 'past') &&
        this.row === 'medication' &&
        this.medicationForm.medication &&
        this.medicationForm.continuous !== null &&
        this.medicationError === null &&
        this.dateError === null
      ) {
        return true
      }
      return false
    },
    canSaveSupplement() {
      if (
        (this.supplementForm.start ||
          this.supplementForm.start_date_part === 'past') &&
        this.row === 'supplement' &&
        this.supplementForm.supplement &&
        this.supplementForm.continuous !== null &&
        this.medicationError === null &&
        this.dateError === null
      ) {
        return true
      }
      return false
    },
    computedRecord: {
      get() {
        if (this.row === 'medication') {
          return this.medicationForm.private
        } else {
          return this.supplementForm.private
        }
      },
      set(value) {
        if (this.row === 'medication') {
          this.medicationForm.private = value
        } else {
          this.supplementForm.private = value
        }
      },
    },
    canShowMedicationText() {
      return (
        this.medicationForm.dosage &&
        this.medicationForm.dosage_form &&
        this.medicationForm.concentration &&
        this.medicationForm.unit &&
        this.medicationForm.frequency &&
        this.medicationForm.interval
      )
    },
    canShowSupplementText() {
      return (
        this.supplementForm.dosage &&
        this.supplementForm.dosage_form &&
        this.supplementForm.concentration &&
        this.supplementForm.unit &&
        this.supplementForm.frequency &&
        this.supplementForm.interval
      )
    },
  },
  watch: {
    medicationForm: {
      handler(newVal, oldVal) {
        if (newVal) {
          this.$emit('update:formChanged', true)
        }
      },
      deep: true,
    },
    supplementForm: {
      handler(newVal, oldVal) {
        if (newVal) {
          if (!newVal.supplement_stop && newVal.supplement_stop_reason !== '') {
            newVal.medication_stop_reason = ''
          }
          this.$emit('update:formChanged', true)
        }
      },
      deep: true,
    },
    'medicationForm.continuous': function(newVal, oldVal) {
      if (newVal === true && oldVal === false) {
        this.medicationForm.medication_stop = null
        this.medicationForm.medication_stop_date = null
        this.medicationForm.medication_stop_date_part = ''
        this.medicationForm.medication_stop_reason = ''
      }
    },
    'medicationForm.medication_stop': function(newVal, oldVal) {
      if (newVal === true && oldVal === false) {
        this.medicationForm.medication_stop_date = null
        this.medicationForm.medication_stop_date_part = ''
        this.medicationForm.medication_stop_reason = ''
      }
    },
    'supplementForm.continuous': function(newVal, oldVal) {
      if (newVal === true && oldVal === false) {
        this.supplementForm.supplement_stop = null
        this.supplementForm.supplement_stop_date = null
        this.supplementForm.supplement_stop_date_part = ''
        this.supplementForm.supplement_stop_reason = ''
      }
    },
    'supplementForm.supplement_stop': function(newVal, oldVal) {
      if (newVal === true && oldVal === false) {
        this.supplementForm.supplement_stop_date = null
        this.supplementForm.supplement_stop_date_part = ''
        this.supplementForm.supplement_stop_reason = ''
      }
    },
    row(newValue, oldValue) {
      if (newValue === 'medication') {
        this.supplementForm.reset()
        this.supplementAttachments = []
        this.haveStoppedSupplement = ''
      } else {
        this.medicationForm.reset()
        this.medicationAttachments = []
        this.haveStoppedMedication = ''
      }
      this.medicationError = null
      this.dateError = null
    },
  },
  async mounted() {
    try {
      if (!this.getMedicationOptions) {
        const medicationOptions = (await medicationService.options()).data
        if (medicationOptions) {
          store.dispatch('options/setMedicationOptions', medicationOptions)
        }
      }

      if (!this.getSupplementOptions) {
        const supplementOptions = (await supplementService.options()).data
        if (supplementOptions) {
          store.dispatch('options/setSupplementOptions', supplementOptions)
        }
      }

      if (this.edit) {
        if (this.item.patient_id !== null) {
          this.showHRActions = false
        }
        this.row = this.item.record_type
        this.itemToForm(this.item)
      }
    } catch (error) {
      console.warn(error)
    }
  },
  methods: {
    ...authMethods,
    clearForm() {
      this.medicationForm.reset()
      this.supplementForm.reset()
      this.medicationAttachments = []
      this.supplementAttachments = []
      this.haveStoppedMedication = ''
      this.haveStoppedSupplement = ''
      this.message = false
      this.isAnother = false
      this.isContinue = false
      this.medicationError = null
      this.dateError = null
    },
    setMedicalDate(medicalDate, past) {
      const field = this.medicalDatePickerName
      let fieldPart
      if (field === 'medication_stop_date') {
        fieldPart = 'medication_stop_date_part'
      } else if (field === 'supplement_stop_date') {
        fieldPart = 'supplement_stop_date_part'
      } else {
        fieldPart = this.medicalDatePickerName + '_date_part'
      }

      const medicalDateObject = medicalDateFormatter(medicalDate)

      if (this.row === 'medication') {
        this.medicationForm[field] = medicalDateObject.date
        this.medicationForm[fieldPart] = this.determinePast(
          past,
          medicalDateObject
        )
      } else {
        this.supplementForm[field] = medicalDateObject.date
        this.supplementForm[fieldPart] = this.determinePast(
          past,
          medicalDateObject
        )
      }
      this.medicalDateStartPickerDialog = false
      this.medicalDateEndPickerDialog = false
    },
    async getMedicationResponse() {
      if (this.edit) {
        return (
          await healthRecordService.update(
            this.item.hr_id,
            this.medicationForm.removeEmptyStringFromData()
          )
        ).data
      } else {
        return (
          await medicationService.create(
            this.medicationForm.removeEmptyFromData()
          )
        ).data
      }
    },
    async getSupplementResponse() {
      if (this.edit) {
        return (
          await healthRecordService.update(
            this.item.hr_id,
            this.supplementForm.removeEmptyStringFromData()
          )
        ).data
      } else {
        return (
          await supplementService.create(
            this.supplementForm.removeEmptyFromData()
          )
        ).data
      }
    },
    async deleteAttachmentMedication(id) {
      try {
        const response = await medicationService.attachmentDelete(id)
        if (response) {
          this.originalMedicationAttachments = this.originalMedicationAttachments.filter(
            function(obj) {
              return obj.id !== id
            }
          )
          this.$emit('update:attachmentDeleted', true)
        }
      } catch (error) {
        console.warn(error)
      }
    },
    async deleteAttachmentSupplement(id) {
      try {
        const response = await supplementService.attachmentDelete(id)
        if (response) {
          this.originalSupplementAttachments = this.originalSupplementAttachments.filter(
            function(obj) {
              return obj.id !== id
            }
          )
          this.$emit('update:attachmentDeleted', true)
        }
      } catch (error) {
        console.warn(error)
      }
    },
    setErrors(error) {
      if (this.row === 'medication') {
        this.medicationForm.onFail(error.response.data)
      } else {
        this.supplementForm.onFail(error.response.data)
      }
      if (this.hasResponse) {
        this.message = true
        this.$emit('update:formSaved', true)
      }
      this.medicationError = error
    },
    async tryToSave(action) {
      this.resetFlagsAndErrors('medicationError', action)
      try {
        let medicationResponse = null
        let supplementResponse = null
        if (this.row === 'medication') {
          if (
            this.medicationForm.start &&
            isBeforeDate(
              this.medicationForm.start,
              new Date().toISOString().split('T')[0]
            )
          ) {
            this.dateError = this.$t('start-after-today')
            this.scrollToErrors()
            return
          }
          if (
            this.medicationForm.start &&
            this.medicationForm.medication_stop_date &&
            isBeforeDate(
              this.medicationForm.start,
              this.medicationForm.medication_stop_date
            )
          ) {
            this.dateError = this.$t('end-before-start')
            this.scrollToErrors()
            return
          }
          if (
            this.medicationForm.start &&
            this.medicationForm.end &&
            isBeforeDate(this.medicationForm.start, this.medicationForm.end)
          ) {
            this.dateError = this.$t('end-before-start')
            this.scrollToErrors()
            return
          }

          medicationResponse = await this.getMedicationResponse()

          if (medicationResponse) {
            this.hasResponse = true
            let responseAttachments

            if (this.medicationAttachments.length > 0) {
              responseAttachments = (
                await medicationService.attachments(
                  this.medicationAttachments,
                  medicationResponse.hr_id,
                  'medication'
                )
              ).data

              console.warn(responseAttachments)
            }
          }
        } else {
          if (
            this.supplementForm.start &&
            isBeforeDate(
              this.supplementForm.start,
              new Date().toISOString().split('T')[0]
            )
          ) {
            this.dateError = this.$t('start-after-today')
            this.scrollToErrors()
            return
          }
          if (
            this.supplementForm.start &&
            this.supplementForm.supplement_stop_date &&
            isBeforeDate(
              this.supplementForm.start,
              this.supplementForm.supplement_stop_date
            )
          ) {
            this.dateError = this.$t('end-before-start')
            this.scrollToErrors()
            return
          }
          if (
            this.supplementForm.start &&
            this.supplementForm.end &&
            isBeforeDate(this.supplementForm.start, this.supplementForm.end)
          ) {
            this.dateError = this.$t('end-before-start')
            return
          }
          supplementResponse = await this.getSupplementResponse()

          if (supplementResponse) {
            this.hasResponse = true
            let responseAttachmentsSupplement

            if (this.supplementAttachments.length > 0) {
              responseAttachmentsSupplement = (
                await supplementService.attachments(
                  this.supplementAttachments,
                  supplementResponse.hr_id,
                  'supplement'
                )
              ).data
              console.warn(responseAttachmentsSupplement)
            }
          }
        }
        if (medicationResponse || supplementResponse) {
          await this.fetchWizardData()

          this.message = true
          if (
            action === 'continue' &&
            store.state.calender.addNewRecord === 'true'
          ) {
            this.$emit('update:formSaved', true)
            this.$emit('close-dialog')
            if (document.getElementById('getAllData'))
              document.getElementById('getAllData').click()
          }
          if (action === 'continue') {
            this.$emit('update:formSaved', true)
            this.edit ? this.$emit('close-dialog') : this.$emit('back')
            if (document.getElementById('getAllData'))
              document.getElementById('getAllData').click()
          }
          if (action === 'another') {
            this.$emit('update:formSavedAnother', true)
            this.clearForm()
            this.$emit('add-another')
            if (document.getElementById('updateGetAllData'))
              document.getElementById('updateGetAllData').click()
          }
        }
      } catch (error) {
        console.warn(error)
        this.setErrors(error)
        this.isAnother = false
        this.isContinue = false
        this.scrollToErrors()
      } finally {
        this.tryingToSave = false
      }
    },
    itemToForm(item) {
      if (this.row === 'medication') {
        this.itemToMedicationForm(item)
      } else {
        this.itemToSupplementForm(item)
      }
    },
    itemToMedicationForm(item) {
      if (item.record.start) {
        this.medicationForm.start = item.record.start
      }
      this.medicationForm.start_date_part = item.record.start_date_part
      if (item.record.end) {
        this.medicationForm.end = item.record.end
      }
      this.medicationForm.continuous = item.record.continuous
      this.medicationForm.end_date_part = item.record.end_date_part
      if (item.record.medication_stop_date) {
        this.medicationForm.medication_stop_date =
          item.record.medication_stop_date
      }
      this.medicationForm.medication_stop_date_part =
        item.record.medication_stop_date_part
      this.medicationForm.medication = item.record.medication
      this.medicationForm.dosage = item.record.dosage
      this.medicationForm.dosage_form = item.record.dosage_form
      this.medicationForm.medication_stop = item.record.medication_stop
      this.medicationForm.medication_stop_reason =
        item.record.medication_stop_reason?.key
      this.medicationForm.frequency = item.record.frequency
      this.medicationForm.interval = item.record.interval
      this.medicationForm.concentration = item.record.concentration
      this.medicationForm.unit = item.record.unit?.key
      this.medicationForm.duration_frequency = item.record.duration_frequency
      this.medicationForm.duration_interval = item.record.duration_interval
      this.medicationForm.note = item.note
      this.medicationForm.private = item.private
      this.originalMedicationAttachments = item.attachments.map((element) => {
        return { ...element }
      })
    },
    itemToSupplementForm(item) {
      if (item.record.start) {
        this.supplementForm.start = item.record.start
      }
      this.supplementForm.start_date_part = item.record.start_date_part
      if (item.record.end) {
        this.supplementForm.end = item.record.end
      }
      this.supplementForm.continuous = item.record.continuous
      this.supplementForm.end_date_part = item.record.end_date_part
      if (item.record.supplement_stop_date) {
        this.supplementForm.supplement_stop_date =
          item.record.supplement_stop_date
      }
      this.supplementForm.supplement_stop_date_part =
        item.record.supplement_stop_date_part
      this.supplementForm.supplement = item.record.supplement
      this.supplementForm.supplement_stop = item.record.supplement_stop
      this.supplementForm.supplement_stop_reason =
        item.record.supplement_stop_reason?.key
      this.supplementForm.dosage = item.record.dosage
      this.supplementForm.dosage_form = item.record.dosage_form?.key
      this.supplementForm.frequency = item.record.frequency
      this.supplementForm.interval = item.record.interval?.key
      this.supplementForm.concentration = item.record.concentration
      this.supplementForm.unit = item.record.unit?.key
      this.supplementForm.duration_frequency = item.record.duration_frequency
      this.supplementForm.duration_interval = item.record.duration_interval
      this.supplementForm.note = item.note
      this.supplementForm.private = item.private
      this.originalSupplementAttachments = item.attachments.map((element) => {
        return { ...element }
      })
    },
  },
}
</script>

<template>
  <v-container @dragover.prevent @drop="onDrop">
    <v-row
      no-gutters
      :class="$style.hrHeader"
      class="primary--text mt-1"
      justify="center"
    >
      <div class="mr-4">
        <v-img
          :src="
            require('@assets/images/safehaven/health-record-icons/medications.svg')
          "
          height="30"
          width="30"
          contain
        ></v-img>
      </div>
      {{ $t('health-record.medications.medication') }}
    </v-row>
    <v-fade-transition mode="out-in">
      <div>
        <v-row
          class="offblack--text"
          :class="$style.hrQuestionFull"
          no-gutters
          justify="center"
        >
          What's the medication's name ?
        </v-row>
        <v-row class="mt-10" no-gutters justify="center">
          <div :class="$style.hrComboBoxContainer">
            <ComboBox
              :key="'medication-combo-1'"
              :disabled="show"
              :combo-class="'medication-combo-1'"
              :value.sync="medicationForm.medication"
              placeholder="e.g. Tylenol"
              :error-messages="
                medicationForm.errors.has('medication')
                  ? 'Medication: ' + medicationForm.errors.get('medication')
                  : ''
              "
              :fetch-url="unauth ? null : '/api/v1/hr/choices/medication/'"
              rounded
              filled
              background-color="input"
              :class="$style.hrComboBox"
              @keydown="
                clearFormFromErrors(
                  'medication',
                  'medicationForm',
                  // eslint-disable-next-line vue/comma-dangle
                  'medicationError'
                )
              "
            ></ComboBox>
          </div>
        </v-row>

        <v-row
          :class="$style.hrQuestionFull"
          class="mt-7 offblack--text"
          no-gutters
          justify="center"
        >
          What amount do you take each time? (e.g. morning or night)
        </v-row>
        <v-row class="mt-10" no-gutters justify="center" align="center">
          <v-col cols="12" md="3">
            <v-row
              class="offblack--text"
              :class="$style.hrQuestionFull"
              no-gutters
              justify="center"
            >
              <v-icon
                :disabled="
                  medicationForm.dosage === '' ||
                    medicationForm.dosage < 1 ||
                    show
                "
                color="primary"
                class="mr-7"
                @click="medicationForm.dosage--"
                >mdi-minus-circle-outline</v-icon
              >
              {{ medicationForm.dosage ? medicationForm.dosage : 0 }}
              <v-icon
                :disabled="show"
                color="primary"
                class="ml-7"
                @click="medicationForm.dosage++"
                >mdi-plus-circle-outline</v-icon
              >
            </v-row>
          </v-col>
          <v-col cols="12" md="3">
            <v-row no-gutters justify="center">
              <v-autocomplete
                v-model="medicationForm.dosage_form"
                :disabled="show"
                class="medicationDosageForm"
                attach=".medicationDosageForm"
                :items="medicationDosageForms"
                item-text="value"
                item-value="key"
                placeholder="e.g. Capsule"
                filled
                rounded
                background-color="input"
                hide-details
                clearable
                :class="[mobile ? 'mt-2' : '', $style.hrAutoCompleteCol]"
              ></v-autocomplete>
            </v-row>
          </v-col>
          <v-col cols="12" md="3">
            <v-row no-gutters justify="center">
              <v-text-field
                v-model="medicationForm.concentration"
                :disabled="show"
                placeholder="e.g. 200"
                rounded
                filled
                hide-details
                background-color="input"
                :class="[mobile ? 'mt-2' : '', $style.hrTextFieldCol]"
                :error-messages="
                  medicationForm.errors.has('concentration')
                    ? 'Concentration: ' +
                      medicationForm.errors.get('concentration')
                    : ''
                "
                @input="
                  clearFormFromErrors(
                    'concentration',
                    'medicationForm',
                    // eslint-disable-next-line vue/comma-dangle
                    'medicationError'
                  )
                "
              />
            </v-row>
          </v-col>
          <v-col cols="12" md="3">
            <v-row no-gutters justify="center">
              <v-autocomplete
                v-model="medicationForm.unit"
                :disabled="show"
                class="medicationUnit"
                attach=".medicationUnit"
                :items="medicationUnits"
                item-text="value"
                item-value="key"
                :placeholder="$t('health-record.medications.unit')"
                rounded
                filled
                hide-details
                background-color="input"
                clearable
                :class="[mobile ? 'mt-2' : '', $style.hrAutoCompleteCol]"
              ></v-autocomplete>
            </v-row>
          </v-col>
        </v-row>

        <v-row
          :class="$style.hrQuestionFull"
          class="mt-10 offblack--text"
          no-gutters
          justify="center"
        >
          How often do you take the above dosage?
        </v-row>
        <v-row class="mt-10" no-gutters justify="center" align="center">
          <v-col cols="12" md="3"></v-col>
          <v-col cols="12" md="3">
            <v-row
              class="offblack--text"
              :class="$style.hrQuestionFull"
              no-gutters
              justify="center"
            >
              <v-icon
                :disabled="
                  medicationForm.frequency === '' ||
                    medicationForm.frequency < 1 ||
                    show
                "
                color="primary"
                class="mr-7"
                @click="medicationForm.frequency--"
                >mdi-minus-circle-outline</v-icon
              >
              {{ medicationForm.frequency ? medicationForm.frequency : 0 }}
              <v-icon
                :disabled="show"
                color="primary"
                class="ml-7"
                @click="medicationForm.frequency++"
                >mdi-plus-circle-outline</v-icon
              >
            </v-row>
          </v-col>
          <v-col cols="12" md="3">
            <v-row no-gutters justify="center" align="center">
              <v-autocomplete
                v-model="medicationForm.interval"
                :disabled="show"
                class="medicationInterval"
                attach=".medicationInterval"
                :items="medicationIntervals"
                item-text="value"
                item-value="key"
                :placeholder="$t('health-record.medications.repeat')"
                rounded
                filled
                hide-details
                background-color="input"
                clearable
                :class="$style.hrAutoCompleteCol"
              ></v-autocomplete>
            </v-row>
          </v-col>
          <v-col cols="12" md="3"></v-col>
        </v-row>

        <div v-if="canShowMedicationText">
          <v-row
            :class="$style.hrQuestionFullItalic"
            class="mt-10 offblack--text"
            no-gutters
            justify="center"
          >
            Ok so,
            {{
              dosageText[medicationForm.dosage] +
                ' ' +
                medicationForm.concentration +
                medicationForm.unit +
                ' ' +
                medicationForm.dosage_form +
                ', ' +
                medicationForm.frequency +
                ' times ' +
                medicationForm.interval
            }}
          </v-row>
        </div>

        <v-row
          :class="$style.hrQuestionFull"
          class="mt-7 offblack--text"
          no-gutters
          justify="center"
        >
          When did you start this medication?
        </v-row>
        <v-row v-if="dateError" class="ml-1">
          <v-col cols="12" md="12">
            <p class="error--text">{{ dateError }}</p>
          </v-col>
        </v-row>
        <v-row class="mt-10" no-gutters justify="center">
          <div
            :class="
              mobile ? $style.medicalDateDivMobile : $style.medicalDateDiv
            "
          >
            <MedicalDatePickerDialog
              :key="'medicationDate-1'"
              :disabled="show"
              :dialog.sync="medicalDateStartPickerDialog"
              :title="'health-record.medications.start-date'"
              :field="medicationForm.start"
              :part="medicationForm.start_date_part"
              filled
              background-color="input"
              :error-messages="
                medicationForm.errors.has('start')
                  ? 'Start Date: ' + medicationForm.errors.get('start')
                  : ''
              "
              @input="clearErrors('start', 'medicationForm', 'medicationError')"
              @setMedicalDate="setMedicalDate"
              @showMedicalDate="showMedicalDate('start')"
            />
          </div>
        </v-row>

        <v-row class="mt-2">
          <v-col cols="12" md="12">
            <v-row
              :class="$style.hrQuestion"
              class="my-auto offblack--text"
              justify="center"
              no-gutters
            >
              Is it a continuous therapy?
            </v-row>
          </v-col>
          <v-row class="mt-2" justify="center">
            <v-radio-group
              v-model="medicationForm.continuous"
              :disabled="show"
              row
              class="my-auto"
              :class="$style.hrRadio"
            >
              <v-row class="my-auto" justify-md="start" justify="center">
                <v-radio label="No" :value="false"></v-radio>
                <v-radio label="Yes" :value="true"></v-radio>
              </v-row>
            </v-radio-group>
          </v-row>
        </v-row>

        <div v-if="medicationForm.continuous === false">
          <v-row
            class="mt-7 offblack--text"
            :class="$style.hrQuestionFull"
            no-gutters
            justify="center"
            >What is the therapy duration?
          </v-row>
          <v-row class="mt-10" no-gutters justify="center">
            <v-col cols="12" md="3"></v-col>
            <v-col cols="12" md="3">
              <v-select
                v-model="medicationForm.duration_frequency"
                :disabled="show"
                class="medicationDurationFrequency"
                attach=".medicationDurationFrequency"
                :items="medicationDurationFrequencies"
                item-text="value"
                item-value="key"
                placeholder="Select Frequency"
                clearable
                filled
                background-color="input"
                rounded
                :class="[!mobile ? 'mr-2' : '', $style.hrSelect]"
              />
            </v-col>
            <v-col cols="12" md="3">
              <v-select
                v-model="medicationForm.duration_interval"
                :disabled="show"
                class="medicationDurationInterval"
                attach=".medicationDurationInterval"
                :items="medicationDurationIntervals"
                item-text="value"
                item-value="key"
                placeholder="Select Interval"
                clearable
                filled
                background-color="input"
                rounded
                :class="[!mobile ? 'ml-2' : '', $style.hrSelect]"
              />
            </v-col>
            <v-col cols="12" md="3"></v-col>
          </v-row>

          <v-row class="mt-10">
            <v-col cols="12" md="6">
              <v-row
                :class="$style.hrQuestion"
                class="my-auto offblack--text"
                justify="center"
                no-gutters
              >
                Did you complete the therapy?
              </v-row>
            </v-col>
            <v-col cols="12" md="6">
              <v-radio-group
                v-model="medicationForm.medication_stop"
                :disabled="show"
                row
                class="my-auto"
                :class="$style.hrRadio"
              >
                <v-row class="my-auto" justify-md="start" justify="center">
                  <v-radio label="No" :value="true"></v-radio>
                  <v-radio label="Yes" :value="false"></v-radio>
                </v-row>
              </v-radio-group>
            </v-col>
          </v-row>

          <div v-if="medicationForm.medication_stop === true">
            <v-row
              :class="$style.hrQuestionFull"
              class="mt-10 offblack--text"
              no-gutters
              justify="center"
            >
              When did you stop taking?
              {{ medicationForm.medication ? medicationForm.medication : '' }}
            </v-row>
            <v-row class="mt-10" no-gutters justify="center">
              <div
                :class="
                  mobile ? $style.medicalDateDivMobile : $style.medicalDateDiv
                "
              >
                <MedicalDatePickerDialog
                  :key="'medicationDate-2'"
                  :disabled="show"
                  :dialog.sync="medicalDateEndPickerDialog"
                  :title="'health-record.medications.end-date'"
                  :field="medicationForm.medication_stop_date"
                  :part="medicationForm.medication_stop_date_part"
                  filled
                  background-color="input"
                  @setMedicalDate="setMedicalDate"
                  @showMedicalDate="showMedicalDate('medication_stop_date')"
                  @input="
                    clearErrors(
                      'medication_stop_date',
                      'medicationForm',
                      // eslint-disable-next-line vue/comma-dangle
                      'medicationError'
                    )
                  "
                />
              </div>
            </v-row>

            <v-row
              :class="$style.hrQuestionFull"
              class="mt-7 offblack--text"
              no-gutters
              justify="center"
            >
              Why did you stop?
            </v-row>
            <v-row class="mt-10" no-gutters justify="center">
              <v-autocomplete
                v-model="medicationForm.medication_stop_reason"
                :disabled="show"
                class="medicationStopReason"
                attach=".medicationStopReason"
                :items="medicationStopReasons"
                item-text="value"
                item-value="key"
                :placeholder="$t('health-record.medications.stop-reason')"
                rounded
                filled
                background-color="input"
                clearable
                :class="$style.hrAutoComplete"
              ></v-autocomplete>
            </v-row>
          </div>
        </div>

        <v-row
          :class="$style.hrQuestionFull"
          class="mt-10 offblack--text"
          no-gutters
          justify="center"
        >
          Any additional files or notes you'd like to include?
        </v-row>
        <v-row class="mt-10">
          <div class="mx-auto" :class="$style.hrInputDiv">
            <SpeechToText
              :key="'medication-2'"
              :disabled="show"
              :value.sync="medicationForm.note"
              auto-grow
              rounded
              :class="$style.hrSpeechToText"
              :placeholder="$t('health-record.medications.note')"
            ></SpeechToText>
          </div>
        </v-row>
        <v-row class="mt-10">
          <div class="mx-auto" :class="$style.hrInputDiv">
            <FileUpload
              :key="'medication-3'"
              :disabled="show"
              icon
              :height="200"
              :multiple="true"
              :files.sync="medicationAttachments"
              :non-draggable="true"
            ></FileUpload>
          </div>
        </v-row>

        <v-row v-if="edit" class="mt-10">
          <div class="mx-auto" :class="$style.hrInputDiv">
            <AttachmentList
              :key="'attachment-list-1'"
              :disabled="show"
              :attachments="originalMedicationAttachments"
              @delete-attachment="deleteAttachmentMedication"
            ></AttachmentList>
          </div>
        </v-row>
      </div>
    </v-fade-transition>

    <div v-if="row !== ''">
      <ForMyEyesOnly v-if="!show" :value.sync="computedRecord" />
      <div v-if="showHRActions">
        <HrActions
          :can-save="canSaveMedication || canSaveSupplement"
          :is-another="isAnother"
          :is-continue="isContinue"
          :trying-to-save="tryingToSave"
          :trying-to-delete="tryingToDelete"
          :edit="edit"
          @go-back="goBack"
          @try-to-save="tryToSave"
          @try-to-delete="prepareDeletion"
        />
      </div>
    </div>

    <v-row v-if="medicationError" class="ml-1">
      <v-col cols="12" md="12">
        <p class="error--text">{{ $t('health-record.medications.error') }}</p>
        <div v-if="medicationForm.errors.has('non_field_errors')">
          <div
            v-for="(error, index) in medicationForm.errors.getNonField(
              // eslint-disable-next-line vue/comma-dangle
              'non_field_errors'
            )"
            :key="'error-non-field' + index"
          >
            <p class="error--text">{{ error }}</p>
          </div>
        </div>
        <div v-if="supplementForm.errors.has('non_field_errors')">
          <div
            v-for="(error, index) in supplementForm.errors.getNonField(
              // eslint-disable-next-line vue/comma-dangle
              'non_field_errors'
            )"
            :key="'error-non-field' + index"
          >
            <p class="error--text">{{ error }}</p>
          </div>
        </div>
      </v-col>
    </v-row>
    <v-row v-if="message" class="ml-1">
      <p class="success--text">{{ $t('health-record.medications.message') }}</p>
    </v-row>
    <BaseConfirmDialog
      v-if="confirmDialog"
      :dialog="confirmDialog"
      :message="computedMessage"
      title="Delete Item"
      @cancel="cancel"
      @confirm="deleteItem"
    />
  </v-container>
</template>

<style lang="scss" module>
@import '@design';
.hrHeader {
  // styleName: Desktop/Display X-Small Bold;
  width: 100%;
  font-family: $system-default-font-family;
  font-size: 24px;
  font-style: normal;
  font-weight: 700;
  line-height: 32px;
  text-align: center;
  letter-spacing: 1px;
}
.hrQuestion {
  // styleName: Desktop/Display X-Small;
  font-family: $system-default-font-family;
  font-size: 22px;
  font-style: normal;
  font-weight: 400;
  line-height: 32px;
  text-align: center;
  letter-spacing: 1px;
}
.hrQuestionFull {
  // styleName: Desktop/Display X-Small;
  width: 100%;
  font-family: $system-default-font-family;
  font-size: 22px;
  font-style: normal;
  font-weight: 400;
  line-height: 32px;
  text-align: center;
  letter-spacing: 1px;
}
.hrQuestionFullItalic {
  font-family: $system-default-font-family;
  font-size: 22px;
  font-style: italic;
  font-weight: 400;
  line-height: 32px;
  text-align: left;
  letter-spacing: 1px;
}
.hrRadio {
  // styleName: Mobile / Display Small;
  font-family: $system-default-font-family;
  font-size: 20px;
  font-style: normal;
  font-weight: 400;
  line-height: 32px;
  text-align: left;
  letter-spacing: 1px;
}
.hrComboBoxContainer {
  width: 394px;
}

.hrComboBox {
  border-radius: 15px !important;
}
.hrTextField {
  max-width: 394px !important;
  border-radius: 15px !important;
}
.hrTextFieldCol {
  max-width: 200px !important;
  border-radius: 15px !important;
}
.hrAutoComplete {
  max-width: 394px !important;
  border-radius: 15px !important;
}
.hrAutoCompleteCol {
  max-width: 200px !important;
  border-radius: 15px !important;
}
.hrSelect {
  max-width: 394px !important;
  border-radius: 15px !important;
}
.hrInputDiv {
  width: 394px;
  border-radius: 15px !important;
}
.hrSpeechToText {
  max-width: 394px !important;
  background: $color-theme-light-input;
}
.hrButton {
  border-radius: 8px !important;
}
</style>
